import React, { useState, useEffect } from 'react';
import { ordersAPI, itemsAPI } from '@/lib/api';
import { getToday } from '@/lib/utils';
import { ShoppingCart, Calendar, Package, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

// Import subcomponents
import {
  DailyOrderTab,
  WeeklyOrderTab,
  SummaryPeriodTab,
  SuggestedOrderTab,
  DiagnosticDialog
} from './orders-provider';

const AdminOrdersProviderPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('daily');
  
  // Daily order states
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [providerOrder, setProviderOrder] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  
  // Weekly order states
  const [weeklySupplier, setWeeklySupplier] = useState('all');
  const [weeklyQuantities, setWeeklyQuantities] = useState({});
  
  // Summary states
  const [summaryPeriod, setSummaryPeriod] = useState('7days');
  const [summaryData, setSummaryData] = useState(null);
  const [summarySupplier, setSummarySupplier] = useState('all');
  const [summaryProduct, setSummaryProduct] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [viewMode, setViewMode] = useState('units');
  const [roundingMode, setRoundingMode] = useState('ceil');
  
  // Suggested order states
  const [suggestedOrder, setSuggestedOrder] = useState(null);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [suggestedViewMode, setSuggestedViewMode] = useState('units');
  const [suggestedSupplier, setSuggestedSupplier] = useState('all');
  
  // Diagnostic states
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [debugSearchTerm, setDebugSearchTerm] = useState('');
  const [debugResult, setDebugResult] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  
  // Shared states
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load items on mount (once)
  useEffect(() => {
    loadAllItems();
  }, []);

  // Load daily order when date changes AND we're on daily tab
  useEffect(() => {
    if (activeTab === 'daily') {
      loadProviderOrder();
    }
  }, [selectedDate, activeTab]);

  // Load suggested order when tab becomes active
  useEffect(() => {
    if (activeTab === 'suggested' && !suggestedOrder) {
      loadSuggestedOrder();
    }
  }, [activeTab]);

  // Load summary when period changes AND we're on summary tab
  useEffect(() => {
    if (activeTab === 'summary') {
      loadSummaryByPeriod();
    }
  }, [summaryPeriod, activeTab]);

  const loadAllItems = async () => {
    try {
      const response = await itemsAPI.getAll(true);
      setAllItems(response.data);
      return response.data;
    } catch (error) {
      console.error('Error loading items:', error);
      return [];
    }
  };

  const loadProviderOrder = async () => {
    setLoading(true);
    try {
      const response = await ordersAPI.getConsolidated(selectedDate);
      const consolidated = response.data;
      
      setProviderOrder({
        order_date: consolidated.order_date,
        items: consolidated.items,
        itemsToOrder: consolidated.items.filter(item => item.final_required > 0),
        total_items: consolidated.items.length,
        generated_at: consolidated.generated_at
      });
    } catch (error) {
      toast.error('Error al cargar pedido a proveedores');
      setProviderOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshWeeklyStock = async () => {
    setLoading(true);
    try {
      await loadAllItems();
      toast.success('Stock actualizado');
    } catch (error) {
      toast.error('Error al actualizar stock');
    } finally {
      setLoading(false);
    }
  };

  const loadSummaryByPeriod = async (period = summaryPeriod) => {
    setLoading(true);
    try {
      let startDate = null;
      let endDate = null;
      
      if (period === 'custom' && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      }
      
      const response = await ordersAPI.getSummaryByPeriod(period, startDate, endDate);
      setSummaryData(response.data);
    } catch (error) {
      toast.error('Error al cargar resumen');
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestedOrder = async () => {
    setSuggestedLoading(true);
    try {
      const response = await ordersAPI.getSuggestedOrder();
      setSuggestedOrder(response.data);
    } catch (error) {
      toast.error('Error al cargar pedido sugerido');
      setSuggestedOrder(null);
    } finally {
      setSuggestedLoading(false);
    }
  };

  const tabs = [
    { id: 'daily', label: 'Pedido del Día', icon: Calendar },
    { id: 'weekly', label: 'Pedido Semanal', icon: Package },
    { id: 'summary', label: 'Resumen por Período', icon: BarChart3 },
    { id: 'suggested', label: 'Pedido Sugerido', icon: ShoppingCart }
  ];

  return (
    <div className="space-y-6" data-testid="admin-orders-provider-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Pedidos a Proveedores</h1>
          <p className="text-muted-foreground mt-1">Gestiona tus pedidos diarios y semanales</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-slate-900'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'daily' && (
        <DailyOrderTab
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedSupplier={selectedSupplier}
          setSelectedSupplier={setSelectedSupplier}
          providerOrder={providerOrder}
          allItems={allItems}
          loading={loading}
          loadProviderOrder={loadProviderOrder}
        />
      )}

      {activeTab === 'weekly' && (
        <WeeklyOrderTab
          allItems={allItems}
          weeklySupplier={weeklySupplier}
          setWeeklySupplier={setWeeklySupplier}
          weeklyQuantities={weeklyQuantities}
          setWeeklyQuantities={setWeeklyQuantities}
          loading={loading}
          refreshWeeklyStock={refreshWeeklyStock}
        />
      )}

      {activeTab === 'summary' && (
        <SummaryPeriodTab
          summaryPeriod={summaryPeriod}
          setSummaryPeriod={setSummaryPeriod}
          summaryData={summaryData}
          summarySupplier={summarySupplier}
          setSummarySupplier={setSummarySupplier}
          summaryProduct={summaryProduct}
          setSummaryProduct={setSummaryProduct}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          viewMode={viewMode}
          setViewMode={setViewMode}
          roundingMode={roundingMode}
          setRoundingMode={setRoundingMode}
          loading={loading}
          loadSummaryByPeriod={loadSummaryByPeriod}
          setDebugDialogOpen={setDebugDialogOpen}
        />
      )}

      {activeTab === 'suggested' && (
        <SuggestedOrderTab
          suggestedOrder={suggestedOrder}
          suggestedLoading={suggestedLoading}
          suggestedViewMode={suggestedViewMode}
          setSuggestedViewMode={setSuggestedViewMode}
          suggestedSupplier={suggestedSupplier}
          setSuggestedSupplier={setSuggestedSupplier}
          loadSuggestedOrder={loadSuggestedOrder}
        />
      )}

      {/* Diagnostic Dialog */}
      <DiagnosticDialog
        open={debugDialogOpen}
        onOpenChange={setDebugDialogOpen}
        debugSearchTerm={debugSearchTerm}
        setDebugSearchTerm={setDebugSearchTerm}
        debugResult={debugResult}
        setDebugResult={setDebugResult}
        debugLoading={debugLoading}
        setDebugLoading={setDebugLoading}
        summaryData={summaryData}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
      />
    </div>
  );
};

export default AdminOrdersProviderPage;
